import {getRegisteredGraphs} from "./graph_registry.js";

const OVERLAY_CLASS = "lqos-time-crosshair-sync";
const MIN_UNIX_SECONDS = 946684800; // 2000-01-01
const MAX_UNIX_SECONDS = 4102444800; // 2100-01-01
const MIN_UNIX_MILLISECONDS = MIN_UNIX_SECONDS * 1000;
const MAX_UNIX_MILLISECONDS = MAX_UNIX_SECONDS * 1000;

const cleanups = new WeakMap();
const axisCache = new WeakMap();
const activeIndexes = new WeakMap();
let activeSourceGraph = null;

function getAxes(option) {
    const axes = option?.xAxis;
    if (!axes) return [];
    return Array.isArray(axes) ? axes : [axes];
}

export function normalizeTimeValue(value) {
    if (value instanceof Date) {
        return value.getTime();
    }
    if (typeof value === "number") {
        if (!Number.isFinite(value)) return null;
        if (value >= MIN_UNIX_MILLISECONDS && value <= MAX_UNIX_MILLISECONDS) {
            return Math.round(value);
        }
        if (value >= MIN_UNIX_SECONDS && value <= MAX_UNIX_SECONDS) {
            return Math.round(value * 1000);
        }
        return null;
    }
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed.length === 0) return null;
        if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
            return normalizeTimeValue(Number(trimmed));
        }
        const parsed = Date.parse(trimmed);
        if (!Number.isNaN(parsed)) {
            return normalizeTimeValue(parsed);
        }
    }
    return null;
}

export function findTimeAxis(option) {
    const axes = getAxes(option);
    for (let axisIndex = 0; axisIndex < axes.length; axisIndex += 1) {
        const axis = axes[axisIndex];
        if (!axis) {
            continue;
        }
        if (axis.type === "category" && Array.isArray(axis.data) && axis.data.length > 0) {
            const normalized = axis.data.map(normalizeTimeValue);
            const validCount = normalized.filter((value) => value !== null).length;
            if (validCount >= Math.max(2, Math.ceil(axis.data.length * 0.6))) {
                return {
                    axisIndex,
                    axis,
                    axisValues: axis.data,
                    timestamps: normalized,
                    tolerance: matchingTolerance(normalized),
                };
            }
            continue;
        }
        if (axis.type === "time") {
            const axisValues = collectTimeAxisSeriesValues(option, axisIndex);
            if (axisValues.length > 0) {
                const normalized = axisValues.map(normalizeTimeValue);
                return {
                    axisIndex,
                    axis,
                    axisValues,
                    timestamps: normalized,
                    tolerance: matchingTolerance(normalized),
                };
            }
        }
    }
    return null;
}

function collectTimeAxisSeriesValues(option, axisIndex) {
    const values = [];
    const seen = new Set();
    const series = Array.isArray(option?.series) ? option.series : [];
    series.forEach((entry) => {
        const seriesAxisIndex = Number(entry?.xAxisIndex || 0);
        if (seriesAxisIndex !== axisIndex || !Array.isArray(entry?.data)) {
            return;
        }
        entry.data.forEach((point) => {
            const value = extractPointTimeValue(point);
            const normalized = normalizeTimeValue(value);
            if (normalized === null || seen.has(normalized)) {
                return;
            }
            seen.add(normalized);
            values.push(value);
        });
    });
    return values;
}

function extractPointTimeValue(point) {
    if (Array.isArray(point)) {
        return point[0];
    }
    if (Array.isArray(point?.value)) {
        return point.value[0];
    }
    return point?.value ?? point?.time ?? point?.timestamp ?? null;
}

export function invalidateTimeCrosshairAxisCache(graph) {
    if (graph) {
        axisCache.delete(graph);
    }
}

function getTimeAxisInfo(graph) {
    const option = graph?.chart?.getOption?.();
    if (!option) return null;
    const cached = axisCache.get(graph);
    if (cached) {
        return cached.axisInfo;
    }
    const axisInfo = findTimeAxis(option);
    axisCache.set(graph, {axisInfo});
    return axisInfo;
}

export function nearestTimeIndex(timestamps, targetTime) {
    if (!Array.isArray(timestamps) || timestamps.length === 0 || targetTime === null) {
        return -1;
    }
    let exactIndex = timestamps.findIndex((value) => value === targetTime);
    if (exactIndex >= 0) {
        return exactIndex;
    }
    let bestIndex = -1;
    let bestDelta = Number.POSITIVE_INFINITY;
    timestamps.forEach((value, index) => {
        if (value === null) return;
        const delta = Math.abs(value - targetTime);
        if (delta < bestDelta) {
            bestDelta = delta;
            bestIndex = index;
        }
    });
    return bestIndex;
}

function nearestTimeMatch(axisInfo, targetTime) {
    const index = nearestTimeIndex(axisInfo?.timestamps, targetTime);
    if (index < 0) return null;
    const delta = Math.abs(axisInfo.timestamps[index] - targetTime);
    const tolerance = axisInfo.tolerance;
    return delta <= tolerance ? {index, delta, tolerance} : null;
}

function matchingTolerance(timestamps) {
    const values = timestamps
        .filter((value) => value !== null)
        .slice()
        .sort((a, b) => a - b);
    if (values.length < 2) {
        return 0;
    }
    const intervals = [];
    for (let i = 1; i < values.length; i += 1) {
        const interval = values[i] - values[i - 1];
        if (interval > 0) intervals.push(interval);
    }
    if (intervals.length === 0) {
        return 0;
    }
    intervals.sort((a, b) => a - b);
    const median = intervals[Math.floor(intervals.length / 2)];
    return Math.min(10 * 60 * 1000, Math.max(90 * 1000, median * 0.55));
}

export function isGraphVisible(graph) {
    const dom = graph?.dom;
    if (!dom || !dom.isConnected) return false;
    if (typeof dom.getClientRects === "function" && dom.getClientRects().length === 0) return false;
    if (typeof window !== "undefined" && typeof window.getComputedStyle === "function") {
        const style = window.getComputedStyle(dom);
        if (style.display === "none" || style.visibility === "hidden") return false;
    }
    const rect = typeof dom.getBoundingClientRect === "function" ? dom.getBoundingClientRect() : null;
    return !rect || (rect.width > 0 && rect.height > 0);
}

function ensureOverlay(graph) {
    if (!graph?.dom) return null;
    let overlay = graph.dom.querySelector(`.${OVERLAY_CLASS}`);
    if (!overlay) {
        overlay = document.createElement("div");
        overlay.className = OVERLAY_CLASS;
        overlay.setAttribute("aria-hidden", "true");
        graph.dom.appendChild(overlay);
    }
    return overlay;
}

function ensureLiveRegion() {
    let region = document.getElementById("lqos-time-crosshair-live-region");
    if (!region) {
        region = document.createElement("div");
        region.id = "lqos-time-crosshair-live-region";
        region.className = "visually-hidden";
        region.setAttribute("aria-live", "polite");
        region.setAttribute("aria-atomic", "true");
        document.body.appendChild(region);
    }
    return region;
}

function announceTime(targetTime) {
    if (typeof document === "undefined" || !document.body) return;
    const region = ensureLiveRegion();
    region.textContent = `Selected chart time ${new Date(targetTime).toLocaleString()}`;
}

function prepareAccessibleGraph(graph) {
    const dom = graph?.dom;
    if (!dom || dom.dataset?.timeCrosshairAccessible === "true") return;
    dom.setAttribute("tabindex", "0");
    const title = dom.closest?.(".dashbox, .zoomed")?.querySelector?.(".dashbox-title");
    if (title?.id) {
        dom.setAttribute("aria-labelledby", title.id);
        dom.setAttribute("aria-description", "Use left and right arrow keys to move the synchronized time marker.");
    } else {
        const titleText = title?.textContent?.trim();
        const label = titleText
            ? `${titleText}. Use left and right arrow keys to move the synchronized time marker.`
            : "Time-series chart. Use left and right arrow keys to move the synchronized time marker.";
        dom.setAttribute("aria-label", label);
    }
    if (dom.dataset) {
        dom.dataset.timeCrosshairAccessible = "true";
    }
}

export function refreshTimeCrosshairAccessibility(graph) {
    const axisInfo = getTimeAxisInfo(graph);
    if (axisInfo) {
        prepareAccessibleGraph(graph);
    }
}

function hideOverlay(graph) {
    const overlay = graph?.dom?.querySelector?.(`.${OVERLAY_CLASS}`);
    if (overlay) {
        overlay.style.opacity = "0";
        overlay.style.transform = "translateX(-9999px)";
    }
}

export function clearSynchronizedTimeMarkers(sourceGraph = null) {
    getRegisteredGraphs({liveOnly: true}).forEach((graph) => {
        if (graph !== sourceGraph) {
            hideOverlay(graph);
        }
    });
}

function isInsideRegisteredGraph(node) {
    if (!node) return false;
    return getRegisteredGraphs({liveOnly: true}).some((graph) => {
        const dom = graph?.dom;
        return dom && (dom === node || dom.contains?.(node));
    });
}

function convertPixel(graph, axisIndex, axisValue, dataIndex) {
    if (!graph?.chart || typeof graph.chart.convertToPixel !== "function") {
        return null;
    }
    const finder = {xAxisIndex: axisIndex};
    const candidates = [axisValue, dataIndex];
    for (const candidate of candidates) {
        try {
            const pixel = graph.chart.convertToPixel(finder, candidate);
            const x = Array.isArray(pixel) ? pixel[0] : pixel;
            if (Number.isFinite(x)) {
                return x;
            }
        } catch (_) {
            // Some ECharts coordinate systems only accept one of category value/index.
        }
    }
    return null;
}

export function showSynchronizedTimeMarker(graph, targetTime) {
    if (!isGraphVisible(graph)) return false;
    const axisInfo = getTimeAxisInfo(graph);
    if (!axisInfo) {
        hideOverlay(graph);
        return false;
    }
    prepareAccessibleGraph(graph);
    const match = nearestTimeMatch(axisInfo, targetTime);
    if (!match) {
        hideOverlay(graph);
        return false;
    }
    const dataIndex = match.index;
    const axisValue = axisInfo.axisValues[dataIndex];
    const x = convertPixel(graph, axisInfo.axisIndex, axisValue, dataIndex);
    if (!Number.isFinite(x)) {
        hideOverlay(graph);
        return false;
    }
    const overlay = ensureOverlay(graph);
    if (!overlay) return false;
    overlay.style.transform = `translateX(${Math.round(x)}px)`;
    overlay.style.opacity = "1";
    return true;
}

function extractPointerTime(graph, event) {
    const axisInfo = getTimeAxisInfo(graph);
    if (!axisInfo || typeof graph.chart?.convertFromPixel !== "function") {
        return undefined;
    }
    const x = Number(event?.offsetX);
    const y = Number(event?.offsetY);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
        return null;
    }
    try {
        const value = graph.chart.convertFromPixel({xAxisIndex: axisInfo.axisIndex}, [x, y]);
        const rawIndex = Array.isArray(value) ? value[0] : value;
        const rawTime = normalizeTimeValue(rawIndex);
        if (rawTime !== null) {
            const match = nearestTimeMatch(axisInfo, rawTime);
            return match ? axisInfo.timestamps[match.index] : null;
        }
        const numericIndex = Number(rawIndex);
        if (Number.isFinite(numericIndex)) {
            const dataIndex = Math.max(0, Math.min(axisInfo.axis.data.length - 1, Math.round(numericIndex)));
            return axisInfo.timestamps[dataIndex];
        }
    } catch (_) {
        return null;
    }
    return null;
}

function updatePeers(sourceGraph, targetTime) {
    if (targetTime === null) {
        clearSynchronizedTimeMarkers(sourceGraph);
        return;
    }
    activeSourceGraph = sourceGraph;
    getRegisteredGraphs({liveOnly: true}).forEach((graph) => {
        if (graph === sourceGraph) return;
        showSynchronizedTimeMarker(graph, targetTime);
    });
}

function updateAllGraphs(sourceGraph, targetTime) {
    activeSourceGraph = sourceGraph;
    activeIndexes.set(sourceGraph, nearestTimeIndex(getTimeAxisInfo(sourceGraph)?.timestamps || [], targetTime));
    announceTime(targetTime);
    getRegisteredGraphs({liveOnly: true}).forEach((graph) => {
        showSynchronizedTimeMarker(graph, targetTime);
    });
}

function moveKeyboardMarker(graph, step) {
    const axisInfo = getTimeAxisInfo(graph);
    if (!axisInfo) return false;
    prepareAccessibleGraph(graph);
    const maxIndex = axisInfo.timestamps.length - 1;
    const currentIndex = activeIndexes.has(graph)
        ? activeIndexes.get(graph)
        : Math.floor(maxIndex / 2);
    const nextIndex = Math.max(0, Math.min(maxIndex, currentIndex + step));
    const targetTime = axisInfo.timestamps[nextIndex];
    if (targetTime === null) return false;
    activeIndexes.set(graph, nextIndex);
    updateAllGraphs(graph, targetTime);
    return true;
}

export function attachTimeCrosshairSync(graph) {
    detachTimeCrosshairSync(graph);
    const zr = graph?.chart?.getZr?.();
    if (!zr || typeof zr.on !== "function") return;

    const onMove = (event) => {
        if (!isGraphVisible(graph)) return;
        const targetTime = extractPointerTime(graph, event);
        if (targetTime === undefined) return;
        updatePeers(graph, targetTime);
    };
    const onClick = (event) => {
        if (!isGraphVisible(graph)) return;
        const targetTime = extractPointerTime(graph, event);
        if (targetTime !== null && targetTime !== undefined) {
            updateAllGraphs(graph, targetTime);
        }
    };
    const onKeyDown = (event) => {
        const keys = {
            ArrowLeft: -1,
            ArrowDown: -1,
            ArrowRight: 1,
            ArrowUp: 1,
            Home: Number.NEGATIVE_INFINITY,
            End: Number.POSITIVE_INFINITY,
        };
        if (!Object.prototype.hasOwnProperty.call(keys, event.key)) return;
        const axisInfo = getTimeAxisInfo(graph);
        if (!axisInfo) return;
        const step = keys[event.key];
        const effectiveStep = step === Number.NEGATIVE_INFINITY
            ? -axisInfo.timestamps.length
            : step === Number.POSITIVE_INFINITY
                ? axisInfo.timestamps.length
                : step;
        if (moveKeyboardMarker(graph, effectiveStep)) {
            event.preventDefault();
        }
    };
    const onLeave = (event) => {
        if (isInsideRegisteredGraph(event?.relatedTarget)) {
            return;
        }
        if (activeSourceGraph === graph) {
            activeSourceGraph = null;
        }
        clearSynchronizedTimeMarkers(graph);
    };

    zr.on("mousemove", onMove);
    zr.on("click", onClick);
    graph.dom?.addEventListener?.("keydown", onKeyDown);
    graph.dom?.addEventListener?.("mouseleave", onLeave);
    cleanups.set(graph, () => {
        if (typeof zr.off === "function") {
            zr.off("mousemove", onMove);
            zr.off("click", onClick);
        }
        graph.dom?.removeEventListener?.("keydown", onKeyDown);
        graph.dom?.removeEventListener?.("mouseleave", onLeave);
        if (activeSourceGraph === graph) {
            activeSourceGraph = null;
            clearSynchronizedTimeMarkers(graph);
        }
        hideOverlay(graph);
    });
}

export function detachTimeCrosshairSync(graph) {
    const cleanup = cleanups.get(graph);
    if (cleanup) {
        cleanup();
        cleanups.delete(graph);
    }
    invalidateTimeCrosshairAxisCache(graph);
}
